import {Checkbox, IconButton, TableCell} from '@mui/material';
import {DeleteOutlined, EditOutlined, LibraryBooksOutlined} from '@mui/icons-material';
import styles from './NewTable.module.scss';
import {ButtonWithConfirm} from 'components/ButtonWithConfirm/ButtonWithConfirm';
import {Row, RowEditType, TableSimpleRowProps} from 'components/NewTable/types';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {SettingsMenu} from '../SettingsMenu/SettingsMenu';
import i18n from 'i18n';

type TableSimpleRowActionsProps<T extends Row> = Omit<TableSimpleRowProps<T>, 'columns'>;

export const TableSimpleRowActions = <T extends Row>({
    tableConfig,
    disableAction,
    isItemSelected,
    handleSelect,
    onEditClick,
    onDelete,
    onDeleteClick,
    row,
    draggableProvided,
    confirmDeletingDescription,
    renderAdditionalRowActions,
    styleProps,
    rowSpan,
    emptyFirstCell,
}: TableSimpleRowActionsProps<T>) => {
    const {canDelete, canEdit, withOrders, editType = RowEditType.INLINE} = tableConfig;
    const editIcon =
        editType === RowEditType.POPUP ? (
            <LibraryBooksOutlined color='primary' />
        ) : (
            <EditOutlined />
        );

    const haveDelete = canDelete && editType === RowEditType.INLINE;

    return (
        <>
            {(haveDelete || canEdit) && (
                <TableCell
                    padding='none'
                    className={styles.sticky}
                    style={styleProps}
                    rowSpan={rowSpan}
                    sx={{
                        backgroundColor: 'var(--mui-palette-background-default) !important',
                    }}
                >
                    {emptyFirstCell && <span style={{display: 'inline-flex', width: 35}}></span>}

                    <span
                        {...draggableProvided?.dragHandleProps}
                        style={{
                            display: withOrders ? 'initial' : 'none',
                            verticalAlign: 'middle',
                        }}
                    >
                        <DragIndicatorIcon color={disableAction ? 'disabled' : 'inherit'} />
                    </span>

                    {haveDelete && handleSelect && (
                        <span>
                            <Checkbox
                                color='primary'
                                disabled={disableAction}
                                checked={isItemSelected}
                                onChange={(event) => {
                                    handleSelect(event, row.id);
                                }}
                                onClick={(event) => event.stopPropagation()}
                            />
                        </span>
                    )}
                    {canEdit && (
                        <span>
                            <IconButton
                                onClick={(event: any) => {
                                    event.stopPropagation();
                                    onEditClick?.(row);
                                }}
                                disabled={disableAction || !onEditClick}
                                color='inherit'
                            >
                                {editIcon}
                            </IconButton>
                        </span>
                    )}
                    {haveDelete && !renderAdditionalRowActions && (
                        <span>
                            <ButtonWithConfirm
                                onClick={async () => {
                                    if (onDeleteClick) {
                                        await onDeleteClick([row.id]);
                                    }
                                }}
                                onConfirm={() => onDelete?.([row.id])}
                                disabled={disableAction}
                                fontSize='medium'
                                description={confirmDeletingDescription}
                            />
                        </span>
                    )}
                    {renderAdditionalRowActions && (
                        <SettingsMenu
                            disabled={disableAction}
                            items={[
                                ...renderAdditionalRowActions({row}),
                                ...(haveDelete
                                    ? [
                                          {
                                              trigger: i18n.t('delete', {ns: 'common'}),
                                              handler: () => {
                                                  onDelete?.([row.id]);
                                              },
                                              icon: <DeleteOutlined color='action' />,
                                          },
                                      ]
                                    : []),
                            ]}
                        />
                    )}
                </TableCell>
            )}
        </>
    );
};
